/**
 * ClassificacaoView — Brief 4 (Gestor) · Tela 6 Configuração de Classificação.
 *
 * READ-ONLY honesta. Renderiza a régua canônica dos 4 níveis de `@lume/shared`
 * (fonte única; rótulos/critérios triangulados de ADR-036/024 + prompt do
 * classification-agent) + a garantia dura do Nível 4 (escala imediata,
 * não-removível, ADR-030 Eixo I).
 *
 * NÃO há CRUD de gatilhos "palavra-chave → nível": o runtime classifica
 * semanticamente via IA e não existe mecanismo/consumidor para keyword→nível
 * (honest-defer — achado (b) em
 * docs/superpowers/audits/2026-06-achado-fiacao-nicho-classification-agent.md).
 * A tabela de "elevação adversarial" do mockup era scaffolding sem fonte
 * (ADR-040 é filtro de visibilidade, não elevação) → omitida.
 */

import { CLASSIFICATION_LEVELS_CATALOG, HARD_ESCALATION_LEVEL } from '@lume/shared';

import type { ClassificationTone } from '@lume/shared';

const TONE_FG: Record<ClassificationTone, string> = {
  success: 'var(--success-fg)',
  neutral: 'var(--fg-1)',
  warning: 'var(--warning-fg)',
  danger: 'var(--danger-fg)',
};

export function ClassificacaoView(): JSX.Element {
  return (
    <div className="classificacao-screen">
      <div className="cl-page">
        <header className="cl-pagehead">
          <div className="cl-pagehead__crumbs">Configuração</div>
          <h1 className="cl-pagehead__title">Configuração de Classificação</h1>
          <p className="cl-pagehead__sub">
            Quatro níveis canônicos de sensibilidade da conversa, classificados automaticamente pela
            IA.
          </p>
        </header>

        <section className="cl-panel" aria-labelledby="cl-niveis-title">
          <h2 id="cl-niveis-title" className="cl-panel__title">
            4 níveis canônicos
          </h2>
          <div className="cl-levels">
            {CLASSIFICATION_LEVELS_CATALOG.map((def) => (
              <article
                key={def.level}
                className={`cl-level cl-level--${def.tone}`}
                data-testid={
                  def.hardEscalation ? 'classificacao-level-hard' : 'classificacao-level'
                }
              >
                <div className="cl-level__num">Nível {def.level}</div>
                <div className="cl-level__label" style={{ color: TONE_FG[def.tone] }}>
                  {def.label}
                </div>
                <p className="cl-level__desc">{def.description}</p>
              </article>
            ))}
          </div>
          <p className="cl-hardnote" data-testid="classificacao-hard-escalation">
            <strong>Nível {HARD_ESCALATION_LEVEL} = P1.</strong> Escala imediata para um humano —
            salvaguarda não-removível da plataforma. O servidor recusa desligá-la; não é só a
            interface que esconde.
          </p>
        </section>

        <section
          className="cl-panel cl-panel--defer"
          aria-labelledby="cl-comodef-title"
          data-testid="classificacao-honest-defer"
        >
          <h2 id="cl-comodef-title" className="cl-panel__title">
            Como a classificação é definida
          </h2>
          <p className="cl-defer__body">
            A Lume classifica a sensibilidade de cada conversa{' '}
            <strong>semanticamente, por inteligência artificial</strong> (o agente lê o contexto da
            conversa), e não por listas de palavras-chave. Por isso não há gatilhos “palavra →
            nível” para cadastrar aqui — termos avulsos não teriam efeito no atendimento real.
          </p>
          <p className="cl-defer__hint">
            A precisão melhora com o contexto do seu negócio: a IA usa o{' '}
            <strong>setor do tenant</strong> (saúde, jurídico, varejo…) e o nicho já configurado nas{' '}
            <strong>Personas</strong> e <strong>Playbooks</strong> para calibrar o que reconhece
            como sensível no seu domínio — não com manutenção de listas de termos.
          </p>
        </section>
      </div>
    </div>
  );
}
